import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery,
  Divider,
  Card,
  CardMedia,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { format } from "date-fns";

import CustomShapedInput from "./CustomShapedInput";
import CustomButton from "./CustomShapedBtn";

const TicketChat = ({
  messages = [],
  onSendMessage,
  ticketStatus = "open",
}) => {
  const { palette, ...theme } = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chatContainerRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const fileInputRef = useRef(null);

  const [newMessage, setNewMessage] = useState("");
  const [attachment, setAttachment] = useState(null);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (endOfMessagesRef?.current) {
      setTimeout(() => {
        endOfMessagesRef?.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [messages]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy h:mm a");
    } catch (error) {
      return dateString;
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0]; // Only take the first file
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith("image/")) {
      alert("Only image files are allowed");
      return;
    }

    // Check file size (limit to 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      alert("Image size should be less than 5MB");
      return;
    }

    const newAttachment = {
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      type: file.type,
      size: file.size,
      file: file,
      preview: URL.createObjectURL(file),
    };

    setAttachment(newAttachment);
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  const sendMessage = () => {
    if (!newMessage.trim() && !attachment) return;
    onSendMessage(newMessage, attachment);
    setNewMessage("");
    setAttachment(null);
  };

  const chatDisabled = ticketStatus === "closed" || ticketStatus === "resolved";

  return (
    <Box
      ref={chatContainerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#212121",
        border: "1px solid #474747",
        borderRadius: "16px",
        padding: "5px",
        gap: 2,
        mb: 3,
        maxHeight: "70vh",
        overflowY: "auto",
      }}
    >
      {messages.map((message) => (
        <Box
          key={message?.id}
          sx={{
            alignSelf:
              message?.sender?.role !== "admin" ? "flex-end" : "flex-start",
            maxWidth: "80%",
            minWidth: isMobile ? "200px" : "300px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Avatar
              src={message?.avatar || null}
              sx={{
                bgcolor: "#000",
                width: 36,
                height: 36,
              }}
            >
              {message?.sender?.role !== "admin" ? (
                <AccountCircleIcon />
              ) : (
                <HeadsetMicIcon />
              )}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  bgcolor:
                    message?.sender?.role !== "admin" ? "#818181" : "#3c3c3c",
                  color: "white",
                  p: 2,
                  borderRadius: 2,
                  wordBreak: "break-word",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 0.5 }}
                >
                  {message?.sender?.role !== "admin" ? "You" : "Support Agent"}
                </Typography>
                <Typography variant="body2">{message.message}</Typography>

                {message?.image && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {/* Display the image directly */}
                    <Card
                      sx={{ maxWidth: "100%", backgroundColor: "transparent" }}
                    >
                      <CardMedia
                        component="img"
                        image={message?.image}
                        alt="Attached image"
                        sx={{
                          borderRadius: 1,
                          maxHeight: 200,
                          width: "auto",
                          objectFit: "contain",
                          cursor: "pointer",
                        }}
                        onClick={() => window.open(message?.image, "_blank")}
                      />
                    </Card>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: "#aaa", ml: 1 }}>
                {formatDate(message?.timestamp)}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
      {/* Invisible scroll anchor */}
      <div ref={endOfMessagesRef} />

      <Divider sx={{ borderColor: "#474747" }} />
      <Typography
        sx={{ color: "#D8D5CD", fontWeight: "600", fontSize: "20px" }}
      >
        {chatDisabled
          ? `This ticket is ${ticketStatus}!`
          : "Reply to this ticket"}
      </Typography>
      {/* Chat Input */}
      {!chatDisabled && (
        <Box sx={{ mb: 4 }}>
          {attachment && (
            <Box sx={{ mb: 2 }}>
              <Card
                sx={{
                  backgroundColor: "#000",
                  border: "1px solid #474747",
                  borderRadius: "8px",
                  maxWidth: 250,
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={attachment.preview}
                    alt={attachment.name}
                    sx={{ objectFit: "cover" }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(0,0,0,0.6)",
                      borderRadius: "50%",
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                    onClick={removeAttachment}
                  >
                    <Typography sx={{ color: "white", fontSize: 16 }}>
                      ×
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 1 }}>
                  <Typography variant="caption" sx={{ color: "#D8D5CD" }}>
                    {attachment.name.length > 25
                      ? attachment.name.substring(0, 22) + "..."
                      : attachment.name}
                  </Typography>
                </Box>
              </Card>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <CustomShapedInput
                component="TextField"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                multiline
                rows={2}
                svgSize="textarea"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                style={{
                  backgroundColor: "#000",
                  borderRadius: "8px",
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />

              <CustomButton
                title={attachment ? "Change Image" : "Add Image"}
                icon={<AttachFileIcon />}
                variant="attachment"
                onClick={() => fileInputRef.current.click()}
              />

              <CustomButton
                title="Send"
                icon={<SendIcon />}
                variant="primary"
                onClick={sendMessage}
                disabled={!newMessage.trim() && !attachment}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TicketChat;
